import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  DriveEta as OutIcon,
  HomeWork as ReturnedIcon,
  Build as MaintenanceIcon,
  LocalCarWash as WashIcon,
  LocalParking as ParkingIcon,
  NoteAdd as NoteIcon,
  Build,
  LocationOn as LocationIcon,
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { Vehicle, VehicleStatus, MaintenanceRecord, Intervention, VehicleConditionAssessment } from '../../types';
import { vehicleService, maintenanceService, interventionService, vehicleNotesService, vehicleConditionAssessmentService } from '../../services/api';
import InterventionManagement from '../../components/FieldAgent/InterventionManagement';
import ConditionAssessmentModal from '../../components/FieldAgent/ConditionAssessmentModal';
import AssessmentHistoryModal from '../../components/FieldAgent/AssessmentHistoryModal';
import CompareAssessmentsModal from '../../components/FieldAgent/CompareAssessmentsModal';
import AssessmentViewModal from '../../components/FieldAgent/AssessmentViewModal';
import { getStatusLabel, getStatusColor } from '../../utils/vehicleUtils';
import StatusUpdateModal from '../../components/FieldAgent/StatusUpdateModal';
import ParkingMapModal from '../../components/FieldAgent/ParkingMapModal';
import NoteForm from '../../components/FieldAgent/NoteForm';
import MaintenanceManagement from '../../components/FieldAgent/MaintenanceManagement';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const VehicleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [assessments, setAssessments] = useState<VehicleConditionAssessment[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showParkingMapModal, setShowParkingMapModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showInterventionModal, setShowInterventionModal] = useState(false);
  const [showConditionAssessmentModal, setShowConditionAssessmentModal] = useState(false);
  const [showAssessmentHistoryModal, setShowAssessmentHistoryModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showAssessmentViewModal, setShowAssessmentViewModal] = useState(false);
  const [assessmentToEdit, setAssessmentToEdit] = useState<VehicleConditionAssessment | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<VehicleConditionAssessment | null>(null);
  const [assessmentsToCompare, setAssessmentsToCompare] = useState<[VehicleConditionAssessment, VehicleConditionAssessment] | null>(null);
  const [quickStatus, setQuickStatus] = useState<VehicleStatus | null>(null);

  useEffect(() => {
    if (id) {
      loadVehicle(parseInt(id));
    }
  }, [id]);

  const loadVehicle = async (vehicleId: number) => {
    try {
      const [vehicleRes, maintenanceRes, interventionRes] = await Promise.all([
        vehicleService.getById(vehicleId),
        maintenanceService.getByVehicle(vehicleId).catch(() => ({ data: { data: [] } })),
        interventionService.getByVehicle(vehicleId).catch(() => ({ data: { data: [] } }))
      ]);
      
      setVehicle(vehicleRes.data.data);
      setMaintenanceRecords(maintenanceRes.data.data || []);
      setInterventions(interventionRes.data.data || []);
      
      // Load notes and assessments
      await Promise.all([
        loadNotes(vehicleId),
        loadAssessments(vehicleId)
      ]);
    } catch (err) {
      console.error('Error loading vehicle:', err);
      setError('Erreur lors du chargement du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const loadNotes = async (vehicleId: number) => {
    setLoadingNotes(true);
    try {
      const response = await vehicleNotesService.getNotes(vehicleId);
      setNotes(response.data.data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadAssessments = async (vehicleId: number) => {
    setLoadingAssessments(true);
    try {
      const response = await vehicleConditionAssessmentService.getAssessments(vehicleId);
      // Get the latest 5 assessments, sorted by date (newest first)
      const allAssessments = response.data.data || [];
      const sortedAssessments = allAssessments.sort((a: VehicleConditionAssessment, b: VehicleConditionAssessment) => {
        const dateA = new Date(a.assessment_date).getTime();
        const dateB = new Date(b.assessment_date).getTime();
        return dateB - dateA; // Newest first
      });
      setAssessments(sortedAssessments.slice(0, 2)); // Show latest 2
    } catch (error) {
      console.error('Error loading assessments:', error);
      setAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleQuickStatus = (status: VehicleStatus) => {
    setQuickStatus(status);
    setShowStatusModal(true);
  };

  const handleSuccess = () => {
    if (id) {
      loadVehicle(parseInt(id));
    }
  };

  const handleParkingReassign = async (vehicleId: string, newSpotId: string) => {
    try {
      await vehicleService.update(parseInt(vehicleId), { place_parking_id: parseInt(newSpotId) });
      // Reload vehicle data
      if (id) {
        await loadVehicle(parseInt(id));
      }
    } catch (error) {
      console.error('Error reassigning parking:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100%', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !vehicle) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Véhicule non trouvé'}</Alert>
        <Button onClick={() => navigate('/field-agent/vehicles')} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        minHeight: '100%',
        pt: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
        pb: { xs: 20, sm: 12 }, // Extra padding to avoid footer overlap on mobile
      }}
    >
      <Container maxWidth="md" disableGutters sx={{ height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/field-agent/vehicles')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Détails Véhicule
        </Typography>
      </Box>

      {/* Vehicle Header with Square Image and Info */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Vehicle Info on the Left */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {vehicle.registration_number}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {vehicle.marque} {vehicle.modele}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {vehicle.year} • {vehicle.fuel_type || 'Diesel'} • {vehicle.transmission_type || 'Auto'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={getStatusLabel(vehicle.status)}
                sx={{
                  bgcolor: getStatusColor(vehicle.status),
                  color: 'white',
                  fontWeight: 500
                }}
              />
              {vehicle.parking_spot && (
                <Chip 
                  label={`Place: ${vehicle.parking_spot.spot_number}`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
          
          {/* Square Vehicle Image on the Right */}
          {vehicle.primary_image_url && (
            <Box sx={{ 
              width: 120, 
              height: 120, 
              borderRadius: 2,
              overflow: 'hidden',
              flexShrink: 0,
              border: '2px solid #e0e0e0'
            }}>
              <img
                src={vehicle.primary_image_url}
                alt={vehicle.registration_number}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Quick Actions */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          ACTIONS RAPIDES
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* État des Lieux - Carte avec image et bouton */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 1,
            }}
          >
            {/* Image de fond - Dashboard futuriste */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '300px',
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/fieldagent/etatsdeslieux.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: 3,
              }}
            >
              {/* Overlay sombre pour meilleure lisibilité */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 0,
                }}
              />
              {/* Texte "ÉTATS DES LIEUX" dans une boîte en haut */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  px: 3,
                  py: 1.5,
                  borderRadius: 1,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                    textAlign: 'center',
                  }}
                >
                  ÉTATS DES LIEUX
                </Typography>
              </Box>
            </Box>
            
            {/* Carte transparente en bas */}
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 2,
                p: 3,
                mt: -3,
                position: 'relative',
                zIndex: 1,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  color: 'text.primary',
                  fontSize: '1rem',
                  fontWeight: 400,
                }}
              >
                Commencer un état des lieux de véhicule.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setShowConditionAssessmentModal(true)}
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: '#9c27b0',
                  color: '#9c27b0',
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 1.5,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(156, 39, 176, 0.04)',
                    borderColor: '#7b1fa2',
                    color: '#7b1fa2',
                  }
                }}
              >
                Commencer
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<OutIcon />}
              onClick={() => handleQuickStatus('out')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Sortir
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ReturnedIcon />}
              onClick={() => handleQuickStatus('returned')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Rentrer
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<MaintenanceIcon />}
              onClick={() => handleQuickStatus('under_maintenance')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Maintenance
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<WashIcon />}
              onClick={() => handleQuickStatus('washing')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Lavage
            </Button>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ParkingIcon />}
            onClick={() => setShowParkingMapModal(true)}
          >
            Changer de place/voir l'emplacement
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<NoteIcon />}
            onClick={() => setShowNoteModal(true)}
          >
            Ajouter Note Technique
          </Button>
        </Box>
      </Paper>

      {/* Information Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          INFORMATIONS
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Place de parking
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {vehicle.parking_spot?.spot_number || '—'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Kilométrage
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {vehicle.mileage.toLocaleString()} km
              </Typography>
            </Box>
          </Box>
          {(vehicle.equipe_assignee || vehicle.insurance_expiry) && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {vehicle.equipe_assignee && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Équipe assignée
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {vehicle.equipe_assignee}
                  </Typography>
                </Box>
              )}
              {vehicle.insurance_expiry && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Assurance expire
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {new Date(vehicle.insurance_expiry).toLocaleDateString('fr-FR')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Maintenance History */}
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MaintenanceIcon color="primary" />
          Historique de maintenance et interventions
        </Typography>
        
        {maintenanceRecords.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Aucune maintenance enregistrée
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {maintenanceRecords.map((record) => (
              <Box
                key={record.id}
                sx={{
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {record.description || record.maintenance_type || 'Maintenance'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        label={record.status}
                        size="small"
                        color={
                          record.status === 'completed' ? 'success' :
                          record.status === 'in_progress' ? 'warning' :
                          record.status === 'scheduled' ? 'info' : 'default'
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body2">
                        {record.maintenance_type}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date de début
                      </Typography>
                      <Typography variant="body2">
                        {new Date(record.start_date).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                    {record.completion_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Date de fin
                        </Typography>
                        <Typography variant="body2">
                          {new Date(record.completion_date).toLocaleDateString('fr-FR')}
                        </Typography>
                      </Box>
                    )}
                    {record.cost && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Coût
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {record.cost.toLocaleString('fr-FR')} €
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {record.service_provider && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Prestataire
                      </Typography>
                      <Typography variant="body2">
                        {record.service_provider}
                      </Typography>
                    </Box>
                  )}
                  {record.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {record.notes}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<MaintenanceIcon />}
            onClick={() => setShowMaintenanceModal(true)}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              },
            }}
          >
            Gérer la maintenance
          </Button>
        </Box>
      </Paper>

      {/* Intervention History */}
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Build color="secondary" />
          Historique Interventions
        </Typography>
        
        {interventions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Aucune intervention enregistrée
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {interventions.map((intervention) => (
              <Box
                key={intervention.id}
                sx={{
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: 'white'
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {intervention.description || intervention.intervention_type || 'Intervention'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip 
                        label={intervention.status}
                        size="small"
                        color={
                          intervention.status === 'completed' ? 'success' :
                          intervention.status === 'in_progress' ? 'warning' :
                          intervention.status === 'scheduled' ? 'info' : 'default'
                        }
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Type
                      </Typography>
                      <Typography variant="body2">
                        {intervention.intervention_type}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date de début
                      </Typography>
                      <Typography variant="body2">
                        {new Date(intervention.start_date).toLocaleDateString('fr-FR')}
                      </Typography>
                    </Box>
                    {intervention.completion_date && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Date de fin
                        </Typography>
                        <Typography variant="body2">
                          {new Date(intervention.completion_date).toLocaleDateString('fr-FR')}
                        </Typography>
                      </Box>
                    )}
                    {intervention.cost && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Coût
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {intervention.cost.toLocaleString('fr-FR')} €
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {intervention.service_provider && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Prestataire
                      </Typography>
                      <Typography variant="body2">
                        {intervention.service_provider}
                      </Typography>
                    </Box>
                  )}
                  {intervention.technician?.name && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Technicien
                      </Typography>
                      <Typography variant="body2">
                        {intervention.technician.name}
                      </Typography>
                    </Box>
                  )}
                  {intervention.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {intervention.notes}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Build />}
            onClick={() => setShowInterventionModal(true)}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
              },
            }}
          >
            Gérer les interventions
          </Button>
        </Box>
      </Paper>

      {/* Notes Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteIcon color="info" />
          Notes
        </Typography>
        
        {loadingNotes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Aucune note pour ce véhicule
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notes.map((note) => (
              <Box
                key={note.id}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  position: 'relative',
                  mb: 2
                }}
              >
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {note.content}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(note.created_at).toLocaleString('fr-FR')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Condition Assessments History Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraIcon color="primary" />
            Historique des États des Lieux
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CameraIcon />}
            onClick={() => setShowConditionAssessmentModal(true)}
            sx={{ 
              backgroundColor: '#ff6b35',
              color: 'white',
              borderColor: '#ff6b35',
              '&:hover': {
                backgroundColor: '#e55a2b',
                borderColor: '#e55a2b',
              }
            }}
          >
            Nouvel État
          </Button>
        </Box>
        
        {loadingAssessments ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : assessments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              📸 Gestion des états des lieux
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Créez et gérez les états des lieux de ce véhicule avec photos et notes détaillées.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<CameraIcon />}
                onClick={() => setShowConditionAssessmentModal(true)}
                sx={{ 
                  backgroundColor: '#ff6b35',
                  '&:hover': { backgroundColor: '#e55a2b' }
                }}
              >
                Créer un État
              </Button>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setShowAssessmentHistoryModal(true)}
              >
                Voir l'Historique
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              {assessments.map((assessment) => {
                const clientDisplayName =
                  assessment.sub_client_display_name ||
                  assessment.sub_client?.display_name ||
                  assessment.sub_client?.name ||
                  [assessment.sub_client?.nom, assessment.sub_client?.prenom].filter(Boolean).join(' ') ||
                  assessment.sub_client?.company_name ||
                  '';
                const parentClientName =
                  assessment.sub_client_client_name ||
                  assessment.sub_client?.client?.name ||
                  assessment.sub_client?.company_name ||
                  '';

                return (
                <Box
                  key={assessment.id}
                  sx={{
                    mb: 3,
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: 'white',
                    p: 3,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#fafafa',
                      boxShadow: 2,
                      borderColor: '#ff6b35'
                    }
                  }}
                  onClick={() => {
                    setSelectedAssessment(assessment);
                    setShowAssessmentViewModal(true);
                  }}
                >
                  {/* Header with Type, Status, and Date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={assessment.assessment_type === 'depart' ? '🚗 Départ' : assessment.assessment_type === 'retour' ? '🏁 Retour' : '📋 Autre'}
                          size="medium"
                          color={assessment.assessment_type === 'depart' ? 'primary' : assessment.assessment_type === 'retour' ? 'secondary' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip
                          label={
                            assessment.status === 'good' ? 'Bon' :
                            assessment.status === 'fair' ? 'Correct' :
                            assessment.status === 'poor' ? 'Mauvais' :
                            assessment.status === 'critical' ? 'Critique' : assessment.status
                          }
                          size="medium"
                          sx={{
                            bgcolor:
                              assessment.status === 'good' ? '#4caf50' :
                              assessment.status === 'fair' ? '#8bc34a' :
                              assessment.status === 'poor' ? '#ff9800' :
                              assessment.status === 'critical' ? '#f44336' : '#2196f3',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {format(new Date(assessment.assessment_date), 'EEEE dd MMMM yyyy', { locale: fr })}
                      </Typography>
                      {(assessment.created_by_user || assessment.created_by_name) && (
                        <Typography variant="body2" color="text.secondary">
                          Par: {assessment.created_by_user?.name || assessment.created_by_user?.email || assessment.created_by_name || 'Utilisateur'}
                        </Typography>
                      )}
                      {(clientDisplayName || parentClientName) && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Client:&nbsp;
                            <strong>{clientDisplayName || 'N/A'}</strong>
                            {parentClientName && parentClientName !== clientDisplayName && (
                              <span> · {parentClientName}</span>
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {assessment.photos && assessment.photos.length > 0 && (
                        <Chip
                          icon={<CameraIcon />}
                          label={`${assessment.photos.length} photo${assessment.photos.length > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {assessment.notes && assessment.notes.length > 0 && (
                        <Chip
                          icon={<NoteIcon />}
                          label={`${assessment.notes.length} note${assessment.notes.length > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      )}
                      {assessment.signature_path && (
                        <Chip
                          label="Signature"
                          size="small"
                          variant="outlined"
                          color="success"
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Photos Preview */}
                  {assessment.photos && assessment.photos.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        📸 Photos ({assessment.photos.length})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {assessment.photos.slice(0, 4).map((photo: any, index: number) => (
                          <Box
                            key={photo.id || index}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '2px solid #e0e0e0',
                              '&:hover': {
                                borderColor: '#ff6b35',
                                transform: 'scale(1.05)',
                                transition: 'all 0.2s'
                              }
                            }}
                          >
                            <img
                              src={photo.file_url || (photo.file_path ? `/api/storage/vehicle-assessments/${photo.file_path}` : photo.url)}
                              alt={photo.description || `Photo ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </Box>
                        ))}
                        {assessment.photos.length > 4 && (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              border: '2px dashed #e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              +{assessment.photos.length - 4}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Notes List */}
                  {assessment.notes && assessment.notes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        📝 Notes ({assessment.notes.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {assessment.notes.slice(0, 3).map((note: any, index: number) => (
                          <Box
                            key={note.id || index}
                            sx={{
                              p: 1,
                              backgroundColor: '#f9f9f9',
                              borderRadius: 1,
                              borderLeft: '3px solid #ff6b35'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {note.type || 'Note'}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {note.content}
                            </Typography>
                          </Box>
                        ))}
                        {assessment.notes.length > 3 && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                            +{assessment.notes.length - 3} autre{assessment.notes.length - 3 > 1 ? 's' : ''} note{assessment.notes.length - 3 > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* General Notes */}
                  {assessment.general_notes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                        📄 Notes générales
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        p: 1.5, 
                        backgroundColor: '#f9f9f9', 
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {assessment.general_notes}
                      </Typography>
                    </Box>
                  )}

                  {/* Damage and Alert Notes */}
                  {(assessment.damage_notes || assessment.alert_notes) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {assessment.damage_notes && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'error.main' }}>
                            ⚠️ Dégâts
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            p: 1, 
                            backgroundColor: '#ffebee', 
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap'
                          }}>
                            {assessment.damage_notes}
                          </Typography>
                        </Box>
                      )}
                      {assessment.alert_notes && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'warning.main' }}>
                            🚨 Alertes
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            p: 1, 
                            backgroundColor: '#fff3e0', 
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap'
                          }}>
                            {assessment.alert_notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )})}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                startIcon={<CameraIcon />}
                onClick={() => setShowConditionAssessmentModal(true)}
                sx={{ 
                  backgroundColor: '#ff6b35',
                  '&:hover': { backgroundColor: '#e55a2b' }
                }}
              >
                Créer un État
              </Button>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setShowAssessmentHistoryModal(true)}
              >
                Voir tout l'Historique
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Modals */}
      {showStatusModal && (
        <StatusUpdateModal
          vehicle={vehicle}
          initialStatus={quickStatus || vehicle.status}
          open={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setQuickStatus(null);
          }}
          onSuccess={() => {
            setShowStatusModal(false);
            setQuickStatus(null);
            handleSuccess();
          }}
        />
      )}

      {showParkingMapModal && (
        <ParkingMapModal
          vehicle={vehicle}
          open={showParkingMapModal}
          onClose={() => setShowParkingMapModal(false)}
          onReassign={handleParkingReassign}
        />
      )}

      {showNoteModal && (
        <NoteForm
          vehicle={vehicle}
          open={showNoteModal}
          onClose={() => setShowNoteModal(false)}
          onSuccess={() => {
            setShowNoteModal(false);
            handleSuccess();
          }}
        />
      )}

      {showMaintenanceModal && vehicle && (
        <MaintenanceManagement
          vehicle={vehicle}
          open={showMaintenanceModal}
          onClose={() => setShowMaintenanceModal(false)}
          onSuccess={() => {
            setShowMaintenanceModal(false);
            handleSuccess();
          }}
        />
      )}

      {showInterventionModal && vehicle && (
        <InterventionManagement
          vehicle={vehicle}
          open={showInterventionModal}
          onClose={() => setShowInterventionModal(false)}
          onSuccess={() => {
            setShowInterventionModal(false);
            handleSuccess();
          }}
        />
      )}

      {/* Condition Assessment Modal */}
      {showConditionAssessmentModal && vehicle && (
        <ConditionAssessmentModal
          vehicle={vehicle}
          open={showConditionAssessmentModal}
          onClose={() => {
            setShowConditionAssessmentModal(false);
            setAssessmentToEdit(null);
          }}
          onSuccess={() => {
            setShowConditionAssessmentModal(false);
            setAssessmentToEdit(null);
            handleSuccess();
            // Reload assessments
            if (id) {
              loadAssessments(parseInt(id));
            }
          }}
          assessmentToEdit={assessmentToEdit}
        />
      )}

      {/* Assessment History Modal */}
      {showAssessmentHistoryModal && vehicle && (
        <AssessmentHistoryModal
          vehicle={vehicle}
          open={showAssessmentHistoryModal}
          onClose={() => setShowAssessmentHistoryModal(false)}
          onEdit={(assessment: VehicleConditionAssessment) => {
            setShowAssessmentHistoryModal(false);
            setAssessmentToEdit(assessment);
            setShowConditionAssessmentModal(true);
          }}
          onCompare={(assessment1: VehicleConditionAssessment, assessment2: VehicleConditionAssessment) => {
            setShowAssessmentHistoryModal(false);
            setAssessmentsToCompare([assessment1, assessment2]);
            setShowCompareModal(true);
          }}
        />
      )}

      {/* Assessment View Modal */}
      {vehicle && (
        <AssessmentViewModal
          assessment={selectedAssessment}
          assessments={assessments}
          open={showAssessmentViewModal}
          onClose={() => {
            setShowAssessmentViewModal(false);
            setSelectedAssessment(null);
          }}
          onEdit={(assessment: VehicleConditionAssessment) => {
            setShowAssessmentViewModal(false);
            setSelectedAssessment(null);
            setAssessmentToEdit(assessment);
            setShowConditionAssessmentModal(true);
          }}
        />
      )}

      {/* Compare Assessments Modal */}
      {showCompareModal && assessmentsToCompare && (
        <CompareAssessmentsModal
          assessment1={assessmentsToCompare[0]}
          assessment2={assessmentsToCompare[1]}
          open={showCompareModal}
          onClose={() => {
            setShowCompareModal(false);
            setAssessmentsToCompare(null);
          }}
          onEdit={(assessment: VehicleConditionAssessment) => {
            setShowCompareModal(false);
            setAssessmentsToCompare(null);
            setAssessmentToEdit(assessment);
            setShowConditionAssessmentModal(true);
          }}
        />
      )}
      </Container>
    </Box>
  );
};

export default VehicleDetail;

